import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlanGate, getPlanGate } from '../lib/planGate';
import PlanLimitDialog from '../components/dialogs/PlanLimitDialog';
import { Loader2, Users, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function GroupInviteAccept() {
  const { token: inviteToken } = useParams();
  const { user, token: authToken } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const planGate = usePlanGate();

  const onAcceptClick = () => {
    if (!authToken) {
      navigate(`/login?redirect=/group-invite/${inviteToken}`);
      return;
    }
    setConfirmOpen(true);
  };

  useEffect(() => {
    // BUG-INV-01: the preview endpoint now requires auth (it returns the
    // inviter/recipient emails). Only fetch once we have a token; otherwise
    // prompt the user to sign in first.
    if (!authToken) {
      setLoading(false);
      return;
    }
    const fetchPreview = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/groups/invitations/${inviteToken}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.detail || 'Invitation not available');
        setPreview(data);
        setError('');
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [inviteToken, authToken]);

  const accept = async () => {
    if (!authToken) {
      // Send to login then come back
      navigate(`/login?redirect=/group-invite/${inviteToken}`);
      return;
    }
    setAccepting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/groups/invitations/${inviteToken}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        const pg = getPlanGate(data);
        if (pg) { planGate.show(pg); return; }
        throw new Error(data?.detail || 'Failed to accept');
      }
      setAccepted(data);
      toast.success(language === 'ar' ? 'تم الانضمام إلى الفريق' : 'Joined the Team');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background to-muted/30">
      <PlanLimitDialog open={planGate.open} onOpenChange={planGate.setOpen} gate={planGate.gate} message={planGate.message} />
      <Card className="max-w-md w-full" data-testid="invite-accept-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            {language === 'ar' ? 'دعوة للانضمام إلى فريق' : 'Team Invitation'}
          </CardTitle>
          <CardDescription>
            {language === 'ar'
              ? 'شارك بيانات CRM والتنبيهات مع أعضاء فريقك.'
              : 'Share CRM data and alerts with your team.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && !accepted && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span data-testid="invite-error">{error}</span>
            </div>
          )}

          {preview && !accepted && (
            <div className="space-y-2 rounded-lg border p-3 bg-muted/30">
              <div className="text-sm">
                <span className="text-muted-foreground">{language === 'ar' ? 'الفريق:' : 'Team:'}</span>{' '}
                <span className="font-semibold" data-testid="invite-group-name">{preview.group_name}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">{language === 'ar' ? 'دعاك:' : 'Invited by:'}</span>{' '}
                <span>{preview.invited_by_name || preview.invited_by_email}</span>
              </div>
              {preview.invited_email && (
                <div className="text-xs text-muted-foreground">
                  {language === 'ar' ? 'هذه الدعوة مخصصة لـ' : 'This invitation is for'} <strong>{preview.invited_email}</strong>
                </div>
              )}
            </div>
          )}

          {accepted && (
            <div className="rounded-lg border border-green-500/40 bg-green-50 dark:bg-green-950/30 p-4 space-y-2 text-sm">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-semibold">
                <Check className="w-4 h-4" />
                {language === 'ar' ? 'تم الانضمام بنجاح' : 'Successfully joined'}
              </div>
              <ul className="text-xs text-muted-foreground space-y-1 ml-6 list-disc">
                <li>{language === 'ar' ? `الفرص المنقولة: ${accepted.merged.opportunities_moved}` : `Opportunities moved: ${accepted.merged.opportunities_moved}`}</li>
                {accepted.merged.opportunities_skipped_duplicates > 0 && (
                  <li>{language === 'ar' ? `تم تخطي ${accepted.merged.opportunities_skipped_duplicates} فرصة مكررة` : `${accepted.merged.opportunities_skipped_duplicates} duplicate(s) skipped`}</li>
                )}
                <li>{language === 'ar' ? `جهات الاتصال المنقولة: ${accepted.merged.contacts_moved}` : `Contacts moved: ${accepted.merged.contacts_moved}`}</li>
                <li>{language === 'ar' ? `التنبيهات المنقولة: ${accepted.merged.alerts_moved}` : `Alerts moved: ${accepted.merged.alerts_moved}`}</li>
              </ul>
            </div>
          )}

          <div className="flex gap-2">
            {!accepted && !error && (
              <Button onClick={onAcceptClick} disabled={accepting} className="w-full" data-testid="accept-invite-btn">
                {accepting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {!authToken
                  ? (language === 'ar' ? 'تسجيل الدخول والقبول' : 'Sign in & Accept')
                  : (language === 'ar' ? 'القبول والانضمام' : 'Accept & Join')}
              </Button>
            )}
            {accepted && (
              <Button onClick={() => navigate('/dashboard/group')} className="w-full" data-testid="view-group-btn">
                {language === 'ar' ? 'عرض الفريق' : 'View team'}
              </Button>
            )}
          </div>

          {!authToken && !accepted && (
            <p className="text-xs text-muted-foreground text-center">
              {language === 'ar'
                ? 'يجب تسجيل الدخول بحساب يخص نفس الشركة لقبول الدعوة.'
                : 'You must sign in with an account that belongs to the same company to accept.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* NEW-7: warn that joining merges & dissolves the user's current workspace */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="join-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {language === 'ar' ? 'تأكيد الانضمام إلى الفريق' : 'Confirm joining this team'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                {language === 'ar'
                  ? `بالانضمام إلى "${preview?.group_name || ''}"، سيتم نقل فرصك وجهات اتصالك وتنبيهاتك إلى هذا الفريق، وسيتم حلّ مساحة عملك الحالية.`
                  : `By joining "${preview?.group_name || ''}", your opportunities, contacts and alerts will be moved into this team, and your current workspace will be dissolved.`}
              </span>
              <span className="block font-medium text-foreground">
                {language === 'ar' ? 'لا يمكن التراجع عن هذا الإجراء.' : 'This action cannot be undone.'}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="join-confirm-cancel">{language === 'ar' ? 'إلغاء' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setConfirmOpen(false); accept(); }}
              data-testid="join-confirm-accept"
            >
              {language === 'ar' ? 'نعم، انضم' : 'Yes, join team'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
